import Tracker from '@openreplay/tracker';
import log from 'loglevel';
import {
  CODEBUILD_BUILD_NUMBER,
  ENABLE_OPEN_REPLAY_IN_DEVELOPMENT,
  IS_DEVELOPMENT,
  OPEN_REPLAY_INGEST_POINT,
  OPEN_REPLAY_PROJECT_KEY,
} from 'api-config.mjs';
import { isBot, isChromeLikeUA, hasNoPluginsAndNoLanguages } from '../utils/isBot';


// OpenReplay session recording. The tracker also records uncaught errors, unhandled
// promise rejections and console output on its own (`captureExceptions` and
// `consoleMethods`, both on by default in @openreplay/tracker 17).
//
// It is started from gatsby-browser.js's onClientEntry, before React renders anything,
// and not from a component. It used to be created by a component inside App, two renders
// after the first commit, so an error thrown while the page first rendered unmounted that
// component before the tracker existed: the crashes most worth recording were the ones it
// could never see. Only the identify step still lives in React (OpenReplayIdentify.js),
// because it needs the session.
//
// Docs:
//   Constructor options: https://docs.openreplay.com/en/sdk/constructor/
//   SDK methods:         https://docs.openreplay.com/en/sdk/methods/
//   npm package:         https://www.npmjs.com/package/@openreplay/tracker

let resolveStarted;
const started = new Promise((resolve) => {
  resolveStarted = resolve;
});
let startCalled = false;

// Resolves with the running tracker once tracker.start() succeeds, or with null when the
// tracker is not running in this browser (a bot, no project key, or start failed). Never
// resolves on the server, where startOpenReplay is never called.
export const whenOpenReplayStarted = () => started;

// Mirrors `LogRocketLazyLoader.shouldCreateLogRocket` so OpenReplay
// honors the same bot/headless detection rules. Keep the two in sync —
// any new bot signal added there should be added here too.
const shouldStartOpenReplay = () => {
  if (!OPEN_REPLAY_PROJECT_KEY) {
    log.debug("OPEN_REPLAY_PROJECT_KEY is not configured for this stage, skipping OpenReplay initialization.");
    return false;
  }

  if (IS_DEVELOPMENT) {
    return ENABLE_OPEN_REPLAY_IN_DEVELOPMENT;
  }

  const { userAgent } = navigator;
  log.debug(`OpenReplay: got userAgent: ${userAgent}`);

  if (userAgent === "cca-app-tester") {
    log.info("Detected cca-app-tester, skipping OpenReplay initialization.");
    return false;
  }

  if (isBot(userAgent)) {
    log.info(`Detected bot user agent, skipping OpenReplay initialization: ${userAgent}`);
    return false;
  }

  // JS-level automation signals (catches bots that spoof user agent)
  if (navigator.webdriver === true) {
    log.info("Detected navigator.webdriver, skipping OpenReplay initialization.");
    return false;
  }

  // Chrome-like UA without the window.chrome object is a strong headless signal.
  if (isChromeLikeUA(userAgent) && typeof window.chrome === 'undefined') {
    log.info("Chrome UA but no window.chrome object, skipping OpenReplay initialization.");
    return false;
  }

  if (hasNoPluginsAndNoLanguages()) {
    log.info("No plugins and no languages detected, skipping OpenReplay initialization.");
    return false;
  }

  return true;
};

// Never throws. It runs in onClientEntry, ahead of everything else that hook starts, so a
// throw from the tracker's constructor would skip the rest of that hook as well as lose
// the recording.
export const startOpenReplay = () => {
  if (startCalled) {
    return;
  }
  startCalled = true;
  try {
    startTracker();
  } catch (err) {
    const reason = err && err.message ? err.message : String(err);
    log.warn(`OpenReplay: initialization failed — ${reason}`);
    resolveStarted(null);
  }
};

const startTracker = () => {
  if (!shouldStartOpenReplay()) {
    resolveStarted(null);
    return;
  }
  log.info(
    `OpenReplay::init() projectKey=${OPEN_REPLAY_PROJECT_KEY} ingestPoint=${OPEN_REPLAY_INGEST_POINT} build=${CODEBUILD_BUILD_NUMBER}`
  );
  const tracker = new Tracker({
    projectKey: OPEN_REPLAY_PROJECT_KEY,
    ingestPoint: OPEN_REPLAY_INGEST_POINT,
    revID: CODEBUILD_BUILD_NUMBER,
    __DISABLE_SECURE_MODE: IS_DEVELOPMENT,
    // Privacy / sanitization options — defaults per OpenReplay tracker docs:
    // https://docs.openreplay.com/en/sdk/constructor/#privacy
    // The text vs. input pairs are independent: input* governs <input>/<textarea>
    // values, text* governs email/number patterns rendered as DOM text.
    defaultInputMode: 0,         // 0=plain, 1=obscured, 2=ignored (doc default: 2)
    obscureInputEmails: false,   // doc default: true
    obscureInputNumbers: false,  // doc default: true
    obscureTextEmails: false,    // doc default: true — masks email-shaped *rendered text* (not just inputs)
    obscureTextNumbers: false,   // doc default: false — kept explicit for parity with the input setting
    respectDoNotTrack: false,
    network: {
      failuresOnly: true,
      // A failed request is recorded with its request headers, minus the ones named
      // here. The first three are the tracker's own default list, which setting this
      // option replaces. `x-auth-token` is ours: on POST /a2/login it is the credential
      // itself, and for the password sign-in (newSessionWithPassword in api.mjs) that
      // is base64 of the email and password, which anyone can decode. A mistyped
      // password is a 401 and a correct one can still meet a 5xx, so without this
      // either would be stored in the recording. The name is in lower case because
      // that is how api.mjs sends it. `x-auth-type` is still recorded, so a replay
      // shows which kind of credential a failed request carried.
      ignoreHeaders: ["cookie", "set-cookie", "authorization", "x-auth-token"],
    },
  });
  // tracker.start() returns a Promise<SuccessfulStart | UnsuccessfulStart>.
  // Failures resolve with { success: false, reason } rather than
  // rejecting, so we must check the result. Only hand the tracker out
  // if start() actually succeeded, so nothing operates on a
  // non-functional tracker. The .catch() guards against truly
  // unexpected rejections.
  tracker.start()
    .then((result) => {
      if (result && result.success === false) {
        log.warn(`OpenReplay: tracker.start() resolved unsuccessfully — ${result.reason}`);
        resolveStarted(null);
        return;
      }
      log.info("OpenReplay: tracker started");
      resolveStarted(tracker);
    })
    .catch((err) => {
      const reason = err && err.message ? err.message : String(err);
      log.warn(`OpenReplay: tracker.start() failed — ${reason}`);
      resolveStarted(null);
    });
  // No cleanup: the tracker is intended to live for the lifetime of
  // the page, and the SDK does not currently expose a documented
  // unmount/stop API.
};
